// src/cockpit4/components/PlansPanel.tsx
// ============================================
// PlansPanel - 战略计划与闭环审核面板
//
// 从 Obsidian Vault 11_Strategy（战略层）读取计划文档，
// 按时间维度（今日/本周/本月/本季/本年/未来）分类展示。
//
// 数据来源：/vault-api/list?dir=11_Strategy（战略层）&ext=.md
// 文档读取：/vault-api/read?path=11_Strategy（战略层）/xxx.md
//
// 闭环状态：从 frontmatter 的 status 字段提取
//   - draft（草案）/ planned（已排期）/ in_progress（执行中）
//   - completed（已完成）/ reviewed（已复盘）
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  ChevronRight,
  X,
  Loader2,
  RefreshCw,
  CheckCircle2,
  Circle,
  CircleDashed,
  PlayCircle,
  FileSearch,
  FileText,
  Sparkles,
  Archive,
  Maximize2,
  ExternalLink,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useCockpit4Store } from "../store";

// ============================================
// 类型
// ============================================

type PlanPeriod = "daily" | "weekly" | "monthly" | "quarterly" | "yearly" | "future";

interface PlanItem {
  /** 文件名（不含路径） */
  filename: string;
  /** 标题（从 frontmatter title 或文件名提取） */
  title: string;
  /** 时间维度（从文件名或 frontmatter 推断） */
  period: PlanPeriod;
  /** 状态（从 frontmatter status 提取） */
  status: "draft" | "planned" | "in_progress" | "completed" | "reviewed" | "unknown";
  /** 更新时间 */
  updatedAt?: string;
  /** 完整内容（点击后加载） */
  content?: string;
  /** Vault 完整路径（用于显示文件位置） */
  vaultPath?: string;
  /** 是否为自动生成 */
  isAutoGenerated?: boolean;
}

interface VaultFileEntry {
  name: string;
  path: string;
  size: number;
  mtime: string;
  isDirectory: boolean;
}

// ============================================
// 配置
// ============================================

const VAULT_STRATEGY_DIR = "11_Strategy（战略层）";
const VAULT_OPERATIONS_DIR = "10_Operations（运营层）";
const VAULT_PROJECT_DIR = "XEAOS/Project";

const PERIOD_CONFIG: Record<PlanPeriod, { label: string; color: string; icon: typeof Calendar }> = {
  daily: { label: "今日", color: "#38BDF8", icon: Calendar },
  weekly: { label: "本周", color: "#34D399", icon: Calendar },
  monthly: { label: "本月", color: "#A78BFA", icon: Calendar },
  quarterly: { label: "本季", color: "#FBBF24", icon: Calendar },
  yearly: { label: "本年", color: "#F87171", icon: Calendar },
  future: { label: "未来", color: "#60A5FA", icon: Calendar },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Circle }> = {
  draft: { label: "草案", color: "#9CA3AF", icon: CircleDashed },
  planned: { label: "已排期", color: "#60A5FA", icon: Circle },
  in_progress: { label: "执行中", color: "#FBBF24", icon: PlayCircle },
  completed: { label: "已完成", color: "#34D399", icon: CheckCircle2 },
  reviewed: { label: "已复盘", color: "#A78BFA", icon: CheckCircle2 },
  unknown: { label: "未知", color: "#6B7280", icon: Circle },
};

// 从文件名推断时间维度
function inferPeriod(filename: string): PlanPeriod {
  const lower = filename.toLowerCase();
  if (lower.includes("daily") || lower.includes("今日") || /\d{4}-\d{2}-\d{2}/.test(filename)) return "daily";
  if (lower.includes("weekly") || lower.includes("周报") || lower.includes("本周")) return "weekly";
  if (lower.includes("monthly") || lower.includes("月报") || lower.includes("本月")) return "monthly";
  if (lower.includes("quarter") || lower.includes("季") || lower.includes("季度")) return "quarterly";
  if (lower.includes("year") || lower.includes("年") || lower.includes("annual")) return "yearly";
  if (lower.includes("future") || lower.includes("vision") || lower.includes("未来") || lower.includes("战略")) return "future";
  return "daily";
}

// 从文件内容提取 frontmatter
function parseFrontmatter(content: string): { title?: string; status?: string; updated?: string } {
  const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (!fmMatch) return {};
  const fm = fmMatch[1];
  const titleMatch = fm.match(/^title:\s*(.+)$/m);
  const statusMatch = fm.match(/^status:\s*(.+)$/m);
  const updatedMatch = fm.match(/^updated:\s*(.+)$/m);
  return {
    title: titleMatch?.[1]?.trim().replace(/^["']|["']$/g, ""),
    status: statusMatch?.[1]?.trim().replace(/^["']|["']$/g, ""),
    updated: updatedMatch?.[1]?.trim().replace(/^["']|["']$/g, ""),
  };
}

/**
 * 从今日日计划内容派生自媒体内容计划（供媒体工作室读取）
 * 提取日计划中的"今日核心目标/任务清单"作为各平台内容主题
 */
function deriveMediaPlanFromDaily(dailyContent: string, date: string): string {
  // 提取任务清单中的条目（- [ ] xxx / - 已完成 xxx）
  const lines = dailyContent.split("\n");
  const tasks: string[] = [];
  for (const line of lines) {
    const m = line.match(/^\s*-\s+\[[ xX]\]\s+(.+)$/);
    if (m && m[1].trim().length > 4) tasks.push(m[1].trim());
    if (tasks.length >= 8) break;
  }
  // 提取标题（## xxx）
  const headings: string[] = [];
  for (const line of lines) {
    const m = line.match(/^#{2,3}\s+(.+)$/);
    if (m && m[1].trim().length > 4 && !/今日|每日/.test(m[1])) headings.push(m[1].trim());
    if (headings.length >= 4) break;
  }

  const platforms = [
    { id: "wechat", name: "微信公众号", type: "article", count: 2 },
    { id: "douyin_enterprise", name: "抖音企业号", type: "video", count: 1 },
    { id: "xiaohongshu", name: "小红书", type: "image_text", count: 2 },
    { id: "zhihu", name: "知乎", type: "article", count: 1 },
    { id: "bilibili", name: "B站", type: "video", count: 1 },
    { id: "live_stream", name: "每日直播", type: "live", count: 1 },
  ];

  const themePool = [...tasks, ...headings];
  if (themePool.length === 0) themePool.push("AI 时代企业自动化运营", "一个人如何经营一家 AI 公司");

  const items: string[] = [];
  let idx = 0;
  for (const p of platforms) {
    for (let i = 0; i < p.count; i++) {
      const theme = themePool[idx % themePool.length];
      idx++;
      items.push(`- platform: ${p.id}`);
      items.push(`  name: ${p.name}`);
      items.push(`  topic: ${theme}`);
      items.push(`  contentType: ${p.type}`);
      items.push(`  status: pending`);
      items.push(`  priority: ${i === 0 ? "high" : "medium"}`);
      items.push(`  note: 由今日日计划自动派生`);
      items.push("");
    }
  }
  return items.join("\n").trim();
}


// ============================================
// 主组件
// ============================================

export default function PlansPanel() {
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePeriod, setActivePeriod] = useState<PlanPeriod>("daily");
  const [preview, setPreview] = useState<{
    title: string;
    content: string;
    loading: boolean;
    vaultPath?: string;
    isAutoGenerated?: boolean;
  } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState<string | null>(null);
  // 媒体工作室联动提示：日计划生成成功且媒体计划也写入成功后置 true
  const [mediaPlanLinked, setMediaPlanLinked] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archiveFiles, setArchiveFiles] = useState<VaultFileEntry[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  // 展开/收起状态：收起时 240px，展开时占满剩余空间
  const [expanded, setExpanded] = useState(false);
  // 存档 Tab：计划存档 / 生成内容历史（脚本+媒体包）
  // 用户偏好：战略计划与闭环审核需要历史记录入口，可展开查看具体文章内容
  const [archiveTab, setArchiveTab] = useState<"plans" | "content">("plans");
  const [contentHistoryFiles, setContentHistoryFiles] = useState<VaultFileEntry[]>([]);
  const [contentHistoryLoading, setContentHistoryLoading] = useState(false);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 同时读取战略层和运营层的文件
      const [strategyResp, opsResp] = await Promise.all([
        fetch(`/vault-api/list?dir=${encodeURIComponent(VAULT_STRATEGY_DIR)}&ext=.md`),
        fetch(`/vault-api/list?dir=${encodeURIComponent(VAULT_OPERATIONS_DIR)}&ext=.md`),
      ]);

      const allFiles: VaultFileEntry[] = [];

      // 战略层文件
      if (strategyResp.ok) {
        const strategyResult = await strategyResp.json();
        if (strategyResult.success) {
          allFiles.push(...(strategyResult.files || []));
        }
      }

      // 运营层文件（递归扫描子目录 Daily/Weekly/Monthly/Journal/Meetings/Tasks）
      if (opsResp.ok) {
        const opsResult = await opsResp.json();
        if (opsResult.success) {
          const opsEntries: VaultFileEntry[] = opsResult.files || [];
          // 运营层下的子目录
          const subdirs = opsEntries.filter((e) => e.isDirectory);
          // 同时加运营层根目录的 .md 文件
          allFiles.push(...opsEntries.filter((e) => !e.isDirectory));
          // 并行扫描子目录
          const subdirResults = await Promise.all(
            subdirs.map((subdir) =>
              fetch(`/vault-api/list?dir=${encodeURIComponent(subdir.path)}&ext=.md`)
                .then((r) => r.json())
                .then((j) => (j.success ? j.files || [] : []))
                .catch(() => [])
            )
          );
          for (const files of subdirResults) {
            allFiles.push(...files);
          }
        }
      }

      // 按修改时间倒序
      allFiles.sort((a, b) => (b.mtime || "").localeCompare(a.mtime || ""));
      // 优先把 Daily 目录的今日文件排前面（确保"今日"Tab 始终能看到当天的计划）
      const todayStr = new Date().toISOString().slice(0, 10);
      allFiles.sort((a, b) => {
        const aIsToday = a.path.includes(`Daily/${todayStr}.md`);
        const bIsToday = b.path.includes(`Daily/${todayStr}.md`);
        if (aIsToday && !bIsToday) return -1;
        if (!aIsToday && bIsToday) return 1;
        return 0;
      });

      // 逐个读取文件 frontmatter（并行，提高上限确保 Daily 归档不被截断）
      const topFiles = allFiles.slice(0, 80);
      const planItems: PlanItem[] = await Promise.all(
        topFiles.map(async (file) => {
          try {
            const pathEncoded = encodeURIComponent(file.path);
            const readResp = await fetch(`/vault-api/read?path=${pathEncoded}`);
            if (!readResp.ok) throw new Error(`HTTP ${readResp.status}`);
            const readResult = await readResp.json();
            const content: string = readResult.content || "";
            const fm = parseFrontmatter(content);
            // 自动生成判定：frontmatter type=daily_plan 或文件名匹配日期模式
            const isAuto = (fm as any).type === 'daily_plan' || (fm as any).type === 'weekly_plan' || (fm as any).type === 'monthly_plan' || /^\d{4}-\d{2}-\d{2}\.md$/.test(file.name);
            return {
              filename: file.name,
              title: fm.title || file.name.replace(/\.md$/i, ""),
              period: inferPeriod(file.name),
              status: (fm.status as PlanItem["status"]) || "unknown",
              updatedAt: fm.updated || file.mtime,
              vaultPath: file.path,
              isAutoGenerated: isAuto,
            };
          } catch {
            return {
              filename: file.name,
              title: file.name.replace(/\.md$/i, ""),
              period: inferPeriod(file.name),
              status: "unknown" as const,
              vaultPath: file.path,
            };
          }
        })
      );
      setPlans(planItems);
    } catch (err: any) {
      setError(err?.message || String(err));
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 生成今日计划并写入 Vault 运营层
   *
   * 白皮书铁律：Obsidian 是唯一大脑，禁止假数据。
   * 流程：
   *   1. 读取 Obsidian Vault 战略层 + 项目层 + 昨日复盘 + 本周进度
   *   2. 调用 HermesRuntime（LLM）基于真实知识库内容生成今日计划
   *   3. 写入 10_Operations/Daily/{date}.md
   *   4. 同步通知媒体工作室（ContentPlanService 降级读取此文件）
   */
  const generateTodayPlan = useCallback(async () => {
    setGenerating(true);
    setGenStatus("正在读取 Obsidian 知识库...");
    // 每次重新生成时重置联动提示状态
    setMediaPlanLinked(false);
    try {
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

      // 确保必要的 Vault 子目录存在（写入前预创建，避免目录缺失导致写入失败）
      const ensureDirs = async () => {
        const dirs = [
          "10_Operations（运营层）/Daily",
          "10_Operations（运营层）/Weekly",
          "10_Operations（运营层）/Monthly",
          "10_Operations（运营层）/Quarterly",
          "10_Operations（运营层）/Content",
        ];
        for (const dir of dirs) {
          try {
            await fetch(`/vault-api/write?path=${encodeURIComponent(`${dir}/.gitkeep`)}`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ content: "" }),
            });
          } catch (e) { /* 忽略错误：目录可能已存在或写入受限 */ }
        }
      };
      await ensureDirs();

      // 0. 重复生成检测：检查今日计划是否已存在
      const checkPath = `${VAULT_OPERATIONS_DIR}/Daily/${today}.md`;
      const checkResp = await fetch(`/vault-api/read?path=${encodeURIComponent(checkPath)}`);
      if (checkResp.ok) {
        const checkJson = await checkResp.json();
        if (checkJson.content && checkJson.content.length > 50) {
          const confirmed = window.confirm(
            `今日计划已生成（${today}.md），是否查看？\n\n点击"确定"查看现有计划，点击"取消"重新生成（将覆盖）。`
          );
          if (confirmed) {
            setGenStatus(`ℹ️ 今日计划已于早前生成，请查看归档列表。`);
            setGenerating(false);
            return;
          }
          setGenStatus("正在覆盖生成今日计划...");
        }
      }

      // 1. 读取战略层文件
      const stratResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(VAULT_STRATEGY_DIR)}&ext=.md`);
      const stratFiles: VaultFileEntry[] = stratResp.ok ? (await stratResp.json()).files || [] : [];

      // 2. 读取项目文件
      const projResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(VAULT_PROJECT_DIR)}&ext=.md`);
      const projFiles: VaultFileEntry[] = projResp.ok ? (await projResp.json()).files || [] : [];

      setGenStatus(`读取到 ${stratFiles.length} 个战略文档 + ${projFiles.length} 个项目...`);

      // 3. 读取战略文档内容（取前 5 个，每个 800 字）
      const stratContents: string[] = [];
      for (const f of stratFiles.slice(0, 5)) {
        const r = await fetch(`/vault-api/read?path=${encodeURIComponent(f.path)}`);
        if (r.ok) {
          const j = await r.json();
          if (j.content) stratContents.push(`### ${f.name}\n${j.content.slice(0, 800)}`);
        }
      }

      // 4. 读取项目内容（取前 8 个）
      const projContents: string[] = [];
      for (const f of projFiles.slice(0, 8)) {
        const r = await fetch(`/vault-api/read?path=${encodeURIComponent(f.path)}`);
        if (r.ok) {
          const j = await r.json();
          if (j.content) {
            const fm = parseFrontmatter(j.content);
            projContents.push(`- **${fm.title || f.name}** — 状态: ${fm.status || "进行中"}`);
          }
        }
      }

      // 5. 读取昨日 Daily（计算完成率 + 待复盘项）
      let yesterdayCompletionRate = 0;
      let yesterdayPendingCount = 0;
      let yesterdayContent = "";
      try {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);
        const yResp = await fetch(`/vault-api/read?path=${encodeURIComponent(`${VAULT_OPERATIONS_DIR}/Daily/${yesterdayStr}.md`)}`);
        if (yResp.ok) {
          const yJson = await yResp.json();
          if (yJson.content) {
            yesterdayContent = yJson.content;
            const doneMatches = yJson.content.match(/- \[x\]/gi);
            const todoMatches = yJson.content.match(/- \[ \]/gi);
            const done = doneMatches?.length || 0;
            const todo = todoMatches?.length || 0;
            const total = done + todo;
            if (total > 0) yesterdayCompletionRate = Math.round((done / total) * 100);
            yesterdayPendingCount = todo;
          }
        }
      } catch { /* 降级 */ }

      // 6. 读取本周 Weekly 文件
      let weeklyCompletedCount = 0;
      try {
        const weeklyListResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(`${VAULT_OPERATIONS_DIR}/Weekly/`)}&ext=.md`);
        if (weeklyListResp.ok) {
          const wJson = await weeklyListResp.json();
          if (wJson.success && wJson.files) {
            const wNow = new Date();
            const wDayOfWeek = wNow.getDay() === 0 ? 7 : wNow.getDay();
            const wMonday = new Date(wNow);
            wMonday.setDate(wNow.getDate() - wDayOfWeek + 1);
            const wMondayStr = wMonday.toISOString().slice(0, 10);
            const wYearStart = new Date(wNow.getFullYear(), 0, 1);
            const wWeekNum = Math.ceil(((wNow.getTime() - wYearStart.getTime()) / 86400000 + wYearStart.getDay() + 1) / 7);
            const wWeekStr = String(wWeekNum).padStart(2, "0");
            const weekFile = (wJson.files as VaultFileEntry[]).find(
              (f) => f.name.includes(`W${wWeekNum}`) || f.name.includes(`W${wWeekStr}`) || f.name.includes(`第${wWeekNum}周`) || f.name.includes(wMondayStr)
            );
            if (weekFile) {
              const wReadResp = await fetch(`/vault-api/read?path=${encodeURIComponent(weekFile.path)}`);
              if (wReadResp.ok) {
                const wReadJson = await wReadResp.json();
                if (wReadJson.content) {
                  const doneMatches = wReadJson.content.match(/- \[x\]/gi);
                  weeklyCompletedCount = doneMatches?.length || 0;
                }
              }
            }
          }
        }
      } catch { /* 降级 */ }

      setGenStatus("正在调用 Hermes 基于知识库生成今日计划...");

      // 7. 调用 HermesRuntime 基于 Obsidian 真实知识库内容生成今日计划（白皮书铁律：禁止假数据）
      let planContent = "";
      try {
        // 等待 RuntimeManager 初始化
        let rmReady = false;
        for (let i = 0; i < 30; i++) {
          if (RuntimeManager.isInitialized()) { rmReady = true; break; }
          await new Promise((r) => setTimeout(r, 300));
        }

        if (rmReady && RuntimeManager.hasRuntime(RuntimeName.Hermes)) {
          // 构建知识库上下文（真实数据，非硬编码）
          const knowledgeContext = [
            `# 战略文档（来自 11_Strategy）`,
            ...stratContents,
            ``,
            `# 项目进度（来自 XEAOS/Project）`,
            ...projContents,
            ``,
            `# 昨日复盘（来自 10_Operations/Daily/${new Date(Date.now() - 86400000).toISOString().slice(0, 10)}.md）`,
            yesterdayContent.slice(0, 1000) || "（昨日无记录）",
            `- 昨日完成率：${yesterdayCompletionRate}%`,
            `- 待复盘项：${yesterdayPendingCount}项`,
            `- 本周累计完成：${weeklyCompletedCount}项`,
          ].join("\n");

          const resp = await RuntimeManager.execute(RuntimeName.Hermes, "generate_daily_plan", {
            date: today,
            knowledgeContext,
            strategyFiles: stratContents,
            projectFiles: projContents,
            yesterdayCompletionRate,
            yesterdayPendingCount,
            weeklyCompletedCount,
          });

          if (resp?.success && resp.data) {
            const data = resp.data as any;
            planContent = typeof data === "string" ? data : data.content || data.plan || "";
          }
        }
      } catch (err) {
        console.warn("[PlansPanel] Hermes 生成失败，降级到模板", err);
      }

      // 8. 如果 Hermes 未就绪或失败，使用基于真实数据的结构化模板（非硬编码假数据）
      if (!planContent) {
        setGenStatus("Hermes 未就绪，使用结构化模板（基于真实知识库数据）...");
        planContent = `---
type: daily_plan
date: ${today}
created_at: ${new Date().toISOString()}
status: planned
owner: Hermes
priority: P0
review_date: ${today}
tags:
  - 运营/日计划
  - hermes/auto-generated
---

# 📅 今日计划 · ${today}

> 数据来源：Obsidian Vault（11_Strategy + XEAOS/Project + 10_Operations）

## 一、战略对齐（来自 Obsidian 11_Strategy）

${stratContents.length > 0 ? stratContents.join("\n\n") : "- 暂无战略文档，请先在 Obsidian 中创建"}

## 二、项目进度跟踪（来自 Obsidian XEAOS/Project）

${projContents.length > 0 ? projContents.join("\n") : "- 暂无项目数据"}

## 三、今日核心目标（3 件事）

> 基于上述战略文档和项目进度，请手动确定今日 3 件最重要的事

- [ ] 待确定 — 参考上方战略对齐
- [ ] 待确定 — 参考上方项目进度
- [ ] 待确定 — 参考上方昨日待复盘

## 四、昨日复盘

- 昨日完成率：${yesterdayCompletionRate}%
- 待复盘事项：${yesterdayPendingCount}项
- 本周累计完成：${weeklyCompletedCount}项

## 五、今日任务清单

- [ ] 09:00 晨会 + 回顾昨日完成情况
- [ ] 10:00 推进核心项目（参考项目进度）
- [ ] 12:00 午休
- [ ] 14:00 内容生产（公众号/抖音/小红书/知乎/B站）
- [ ] 16:00 客户跟进
- [ ] 18:00 日终复盘 + 写入 Journal
- [ ] 20:00 知识沉淀 + Cards 更新

---

*由 XEAOS 自动生成 · 数据来源：Obsidian Vault · Hermes Runtime*
`;
      }

      // 9. 写入 Vault 运营层
      setGenStatus("正在写入 Obsidian Vault...");
      const writePath = `${VAULT_OPERATIONS_DIR}/Daily/${today}.md`;
      const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(writePath)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: planContent }),
      });

      if (writeResp.ok) {
        const writeResult = await writeResp.json();
        if (writeResult.success) {
          setGenStatus(`✅ 已基于 Obsidian 知识库生成今日计划，写入 ${writePath}`);
          // 联动媒体工作室：从今日计划派生自媒体内容计划并写入
          try {
            const contentPlan = deriveMediaPlanFromDaily(planContent, today);
            const mediaPlanPath = `${VAULT_OPERATIONS_DIR}/Content/每日自媒体计划-${today}.md`;
            const mediaPlanContent = [
              `---`,
              `type: content_plan`,
              `date: ${today}`,
              `created_at: ${new Date().toISOString()}`,
              `status: planned`,
              `source: daily_plan`,
              `---`,
              ``,
              `# 📱 每日自媒体计划 · ${today}`,
              ``,
              `> 由「生成今日计划」自动派生，媒体工作室将按此计划生成内容`,
              ``,
              contentPlan,
            ].join("\n");
            const mediaResp = await fetch(`/vault-api/write?path=${encodeURIComponent(mediaPlanPath)}`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ content: mediaPlanContent }),
            }).catch(() => null);
            // 判断媒体计划是否写入成功，成功则触发联动提示
            let mediaOk = false;
            if (mediaResp && mediaResp.ok) {
              try {
                const mediaJson = await mediaResp.json();
                mediaOk = !!mediaJson.success;
              } catch { /* 解析失败视为未成功 */ }
            }
            if (mediaOk) {
              setMediaPlanLinked(true);
              setGenStatus(`✅ 今日计划已生成，并已联动媒体工作室（${mediaPlanPath}）`);
            } else {
              setGenStatus(`✅ 今日计划已生成（媒体计划联动失败，可手动在媒体工作室创建）`);
            }
          } catch (e) {
            console.warn("[PlansPanel] 媒体计划联动失败（不影响日计划生成）:", e);
          }
          setTimeout(() => loadPlans(), 500);
        } else {
          setGenStatus(`❌ 写入失败：${writeResult.error}`);
        }
      } else {
        setGenStatus(`❌ 写入失败：HTTP ${writeResp.status}`);
      }
    } catch (err: any) {
      setGenStatus(`❌ 生成失败：${err?.message || err}`);
    } finally {
      setGenerating(false);
    }
  }, [loadPlans]);

  /**
   * 加载 Obsidian 存档：读取 10_Operations/Daily/ 目录下所有日计划
   * 同时读取 10_Operations/Weekly/ 和 10_Operations/Monthly/ 子目录
   */
  const loadArchive = useCallback(async () => {
    setArchiveLoading(true);
    try {
      const dailyDir = `${VAULT_OPERATIONS_DIR}/Daily`;
      const weeklyDir = `${VAULT_OPERATIONS_DIR}/Weekly`;
      const monthlyDir = `${VAULT_OPERATIONS_DIR}/Monthly`;

      const [dailyResp, weeklyResp, monthlyResp] = await Promise.all([
        fetch(`/vault-api/list?dir=${encodeURIComponent(dailyDir)}&ext=.md`).catch(() => null),
        fetch(`/vault-api/list?dir=${encodeURIComponent(weeklyDir)}&ext=.md`).catch(() => null),
        fetch(`/vault-api/list?dir=${encodeURIComponent(monthlyDir)}&ext=.md`).catch(() => null),
      ]);

      const allFiles: VaultFileEntry[] = [];

      if (dailyResp?.ok) {
        const j = await dailyResp.json();
        if (j.success) allFiles.push(...(j.files || []));
      }
      if (weeklyResp?.ok) {
        const j = await weeklyResp.json();
        if (j.success) allFiles.push(...(j.files || []));
      }
      if (monthlyResp?.ok) {
        const j = await monthlyResp.json();
        if (j.success) allFiles.push(...(j.files || []));
      }

      // 按修改时间倒序
      allFiles.sort((a, b) => (b.mtime || "").localeCompare(a.mtime || ""));
      setArchiveFiles(allFiles);
    } catch (err) {
      console.warn("[PlansPanel] 加载存档失败", err);
      setArchiveFiles([]);
    } finally {
      setArchiveLoading(false);
    }
  }, []);

  /** 打开存档查看器 */
  const openArchive = async () => {
    setShowArchive(true);
    await loadArchive();
    // 同时预加载生成内容历史
    loadContentHistory();
  };

  /**
   * 加载生成内容历史：读取 XEAOS/Daily/Scripts/ 和 XEAOS/ContentPackages/ 目录
   * 用户偏好：战略计划与闭环审核需要历史记录入口，展开后可查看具体文章内容
   */
  const loadContentHistory = useCallback(async () => {
    setContentHistoryLoading(true);
    try {
      const scriptsRoot = "XEAOS/Daily/Scripts";
      const packagesRoot = "XEAOS/ContentPackages";
      const allFiles: VaultFileEntry[] = [];

      // 递归扫描 Scripts 目录（按日期子目录）
      try {
        const scriptsResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(scriptsRoot)}`);
        if (scriptsResp.ok) {
          const j = await scriptsResp.json();
          if (j.success && Array.isArray(j.files)) {
            // 可能是日期子目录，需要递归
            const entries: VaultFileEntry[] = j.files || [];
            const subdirs = entries.filter((e) => e.isDirectory);
            const rootMdFiles = entries.filter((e) => !e.isDirectory);
            allFiles.push(...rootMdFiles);
            // 并行扫描日期子目录
            const subdirResults = await Promise.all(
              subdirs.map((sd) =>
                fetch(`/vault-api/list?dir=${encodeURIComponent(sd.path)}&ext=.md`)
                  .then((r) => r.json())
                  .then((jj) => (jj.success ? jj.files || [] : []))
                  .catch(() => [])
              )
            );
            for (const files of subdirResults) {
              allFiles.push(...files);
            }
          }
        }
      } catch {
        // 目录不存在
      }

      // 递归扫描 ContentPackages 目录
      try {
        const pkgResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(packagesRoot)}`);
        if (pkgResp.ok) {
          const j = await pkgResp.json();
          if (j.success && Array.isArray(j.files)) {
            const entries: VaultFileEntry[] = j.files || [];
            const subdirs = entries.filter((e) => e.isDirectory);
            const rootMdFiles = entries.filter((e) => !e.isDirectory);
            allFiles.push(...rootMdFiles);
            const subdirResults = await Promise.all(
              subdirs.map((sd) =>
                fetch(`/vault-api/list?dir=${encodeURIComponent(sd.path)}&ext=.md`)
                  .then((r) => r.json())
                  .then((jj) => (jj.success ? jj.files || [] : []))
                  .catch(() => [])
              )
            );
            for (const files of subdirResults) {
              allFiles.push(...files);
            }
          }
        }
      } catch {
        // 目录不存在
      }

      // 按修改时间倒序
      allFiles.sort((a, b) => (b.mtime || "").localeCompare(a.mtime || ""));
      setContentHistoryFiles(allFiles);
    } catch (err) {
      console.warn("[PlansPanel] 加载生成内容历史失败", err);
      setContentHistoryFiles([]);
    } finally {
      setContentHistoryLoading(false);
    }
  }, []);

  /** 查看存档文件内容 */
  const openArchiveFile = async (file: VaultFileEntry) => {
    setPreview({ title: file.name.replace(/\.md$/i, ""), content: "", loading: true });
    try {
      const pathEncoded = encodeURIComponent(file.path);
      const resp = await fetch(`/vault-api/read?path=${pathEncoded}`);
      const result = await resp.json();
      setPreview({ title: file.name.replace(/\.md$/i, ""), content: result.content || "暂无内容", loading: false });
    } catch (err: any) {
      setPreview({ title: file.name, content: `加载失败：${err?.message || err}`, loading: false });
    }
  };

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // 按时间维度分组
  const filteredPlans = plans.filter((p) => p.period === activePeriod);

  // 点击查看计划详情
  const openPlan = async (plan: PlanItem) => {
    setPreview({ title: plan.title, content: "", loading: true });
    try {
      // 优先使用 vaultPath（完整路径），降级到战略目录拼接
      const path = plan.vaultPath || `${VAULT_STRATEGY_DIR}/${plan.filename}`;
      const pathEncoded = encodeURIComponent(path);
      const resp = await fetch(`/vault-api/read?path=${pathEncoded}`);
      const result = await resp.json();
      setPreview({
        title: plan.title,
        content: result.content || "暂无内容",
        loading: false,
        vaultPath: path,
        isAutoGenerated: plan.isAutoGenerated,
      });
    } catch (err: any) {
      setPreview({ title: plan.title, content: `加载失败：${err?.message || err}`, loading: false });
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className={`flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden transition-all duration-300 ${
          expanded ? "flex-1 min-h-0" : "flex-shrink-0 h-[280px]"
        }`}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-purple-400" />
            <span className="text-[13px] font-medium text-white">战略计划与闭环审核</span>
            <span className="text-[9px] font-mono text-gray-600">11_Strategy + 10_Operations</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={generateTodayPlan}
              disabled={generating}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 text-[9px] font-mono text-purple-300 transition-colors disabled:opacity-50"
            >
              {generating ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
              {generating ? "生成中..." : "生成今日计划"}
            </button>
            <button
              onClick={openArchive}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-[9px] font-mono text-cyan-300 transition-colors"
            >
              <Archive size={9} />
              计划存档
            </button>
            <button
              onClick={loadPlans}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-white/[0.06] hover:bg-white/[0.04] text-[9px] font-mono text-gray-400 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={9} className={loading ? "animate-spin" : ""} />
              刷新
            </button>
            {/* 展开/收起按钮 */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-white/[0.06] hover:bg-white/[0.04] text-[9px] font-mono text-gray-400 transition-colors"
              title={expanded ? "收起" : "展开（查看更多内容）"}
            >
              <Maximize2 size={9} />
              {expanded ? "收起" : "展开"}
            </button>
          </div>
        </div>

        {/* 时间维度 Tab */}
        <div className="flex items-center gap-1 px-3 py-1.5 border-b border-white/[0.04]">
          {(Object.keys(PERIOD_CONFIG) as PlanPeriod[]).map((period) => {
            const cfg = PERIOD_CONFIG[period];
            const count = plans.filter((p) => p.period === period).length;
            const active = activePeriod === period;
            return (
              <button
                key={period}
                onClick={() => setActivePeriod(period)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono transition-colors ${
                  active ? "text-white" : "text-gray-500 hover:text-gray-300"
                }`}
                style={active ? { background: `${cfg.color}20`, color: cfg.color } : {}}
              >
                {cfg.label}
                {count > 0 && (
                  <span className="text-[8px] px-1 rounded" style={{ background: `${cfg.color}30` }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* 当前 Tab 说明（仅"今日"显示） */}
        {activePeriod === "daily" && (
          <div className="px-3 py-1 border-b border-white/[0.04] bg-cyan-500/[0.02]">
            <span className="text-[9px] font-mono text-cyan-300">
              📋 今日计划：来自 <span className="text-cyan-400">10_Operations/Daily/</span> 目录的当日 .md 文件，点击"生成今日计划"自动创建
            </span>
          </div>
        )}
        {activePeriod === "weekly" && (
          <div className="px-3 py-1 border-b border-white/[0.04] bg-emerald-500/[0.02]">
            <span className="text-[9px] font-mono text-emerald-300">
              📋 本周计划：来自 <span className="text-emerald-400">10_Operations/Weekly/</span> 目录
            </span>
          </div>
        )}
        {activePeriod === "monthly" && (
          <div className="px-3 py-1 border-b border-white/[0.04] bg-purple-500/[0.02]">
            <span className="text-[9px] font-mono text-purple-300">
              📋 本月计划：来自 <span className="text-purple-400">10_Operations/Monthly/</span> 目录
            </span>
          </div>
        )}
        {(activePeriod === "quarterly" || activePeriod === "yearly" || activePeriod === "future") && (
          <div className="px-3 py-1 border-b border-white/[0.04] bg-amber-500/[0.02]">
            <span className="text-[9px] font-mono text-amber-300">
              📋 {PERIOD_CONFIG[activePeriod].label}计划：来自 <span className="text-amber-400">11_Strategy（战略层）/</span> 目录
            </span>
          </div>
        )}

        {/* 生成状态提示 */}
        {genStatus && (
          <div className="px-3 py-1 border-b border-white/[0.04] bg-purple-500/[0.05]">
            <span className="text-[9px] font-mono text-purple-300">{genStatus}</span>
          </div>
        )}

        {/* 媒体工作室联动提示卡片：日计划成功派生自媒体计划后显示 */}
        {mediaPlanLinked && (
          <div className="px-3 py-2 border-b border-white/[0.04] bg-emerald-500/[0.05] flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />
              <span className="text-[10px] font-mono text-emerald-300 truncate">
                ✅ 今日计划已生成，并已关联到媒体工作室每日自媒体计划
              </span>
            </div>
            <button
              onClick={() => useCockpit4Store.getState().setWorkspace("media_studio")}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-[9px] font-mono text-emerald-300 transition-colors flex-shrink-0"
              title="跳转到媒体工作室查看每日自媒体计划"
            >
              <ExternalLink size={9} />
              查看媒体工作室
            </button>
          </div>
        )}

        {/* 计划列表 */}
        <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-[11px] text-gray-500">
              <Loader2 size={12} className="animate-spin mr-2" />
              正在从 Vault 加载...
            </div>
          ) : error ? (
            <div className="text-center py-6 text-[11px] text-red-400">
              加载失败：{error}
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-gray-600">
              <FileSearch size={20} className="mb-1 opacity-50" />
              <span className="text-[10px] font-mono">暂无{PERIOD_CONFIG[activePeriod].label}计划</span>
            </div>
          ) : (
            filteredPlans.map((plan, i) => {
              const statusCfg = STATUS_CONFIG[plan.status] || STATUS_CONFIG.unknown;
              const StatusIcon = statusCfg.icon;
              return (
                <button
                  key={`plan-${i}`}
                  onClick={() => openPlan(plan)}
                  className="w-full text-left rounded border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5 hover:border-purple-500/30 hover:bg-purple-500/[0.03] transition-colors group cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusIcon size={11} style={{ color: statusCfg.color }} />
                      <span className="text-[11px] text-gray-300 truncate">{plan.title}</span>
                      {/* 自动生成标识 */}
                      {plan.isAutoGenerated && (
                        <span
                          className="text-[8px] font-mono px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-400 flex-shrink-0"
                          title="此文件由系统自动生成"
                        >
                          自动
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[9px] font-mono px-1 py-0.5 rounded" style={{ background: `${statusCfg.color}20`, color: statusCfg.color }}>
                        {statusCfg.label}
                      </span>
                      <ChevronRight size={10} className="text-gray-600 group-hover:text-purple-400 transition-colors" />
                    </div>
                  </div>
                  {/* 显示文件路径，让用户知道看哪个文件 */}
                  {plan.vaultPath && (
                    <div className="text-[9px] font-mono text-gray-600 mt-0.5 ml-5 truncate" title={plan.vaultPath}>
                      📁 {plan.vaultPath}
                    </div>
                  )}
                  {plan.updatedAt && (
                    <div className="text-[9px] font-mono text-gray-600 mt-0.5 ml-5">
                      更新于 {plan.updatedAt}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </motion.div>

      {/* 计划详情 Modal */}
      <AnimatePresence>
        {preview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setPreview(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-xl border border-white/10 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-purple-400" />
                  <h3 className="text-[14px] font-semibold text-white">{preview.title}</h3>
                </div>
                <button
                  onClick={() => setPreview(null)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-5">
                {preview.loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 size={16} className="animate-spin mr-2" />
                    <span className="text-[12px]">正在从 Obsidian Vault 加载...</span>
                  </div>
                ) : (
                  <pre className="text-[12px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {preview.content}
                  </pre>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[9px] font-mono text-gray-600">
                  数据来源：Obsidian Vault · 11_Strategy（战略层）
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ 计划存档全屏 Modal ============ */}
      <AnimatePresence>
        {showArchive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-6"
            onClick={() => setShowArchive(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-5xl max-h-[90vh] flex flex-col rounded-xl border border-cyan-500/20 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 头部 */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06] bg-cyan-500/[0.03]">
                <div className="flex items-center gap-2">
                  <Archive size={16} className="text-cyan-400" />
                  <h3 className="text-[15px] font-semibold text-white">历史记录</h3>
                  <span className="text-[10px] font-mono text-gray-500">
                    {archiveTab === "plans"
                      ? `计划存档 · ${archiveFiles.length} 篇`
                      : `生成内容 · ${contentHistoryFiles.length} 篇`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (archiveTab === "plans") loadArchive();
                      else loadContentHistory();
                    }}
                    disabled={archiveTab === "plans" ? archiveLoading : contentHistoryLoading}
                    className="flex items-center gap-1 px-2 py-1 rounded border border-white/[0.06] hover:bg-white/[0.04] text-[10px] font-mono text-gray-400 transition-colors"
                  >
                    <RefreshCw size={10} className={(archiveTab === "plans" ? archiveLoading : contentHistoryLoading) ? "animate-spin" : ""} />
                    刷新
                  </button>
                  <button
                    onClick={() => setShowArchive(false)}
                    className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Tab 切换：计划存档 / 生成内容历史 */}
              <div className="flex items-center gap-1 px-5 py-2 border-b border-white/[0.04] bg-white/[0.01]">
                <button
                  onClick={() => setArchiveTab("plans")}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-mono transition-colors ${
                    archiveTab === "plans"
                      ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                      : "text-gray-500 hover:text-gray-300 border border-transparent"
                  }`}
                >
                  <Calendar size={11} /> 计划存档
                </button>
                <button
                  onClick={() => setArchiveTab("content")}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-mono transition-colors ${
                    archiveTab === "content"
                      ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                      : "text-gray-500 hover:text-gray-300 border border-transparent"
                  }`}
                >
                  <FileText size={11} /> 生成内容历史
                </button>
              </div>

              {/* 列表区域 */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                {archiveTab === "plans" ? (
                  /* 计划存档列表 */
                  archiveLoading ? (
                    <div className="flex items-center justify-center py-12 text-gray-500">
                      <Loader2 size={16} className="animate-spin mr-2" />
                      <span className="text-[12px]">正在从 Obsidian Vault 加载存档...</span>
                    </div>
                  ) : archiveFiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-600">
                      <FileSearch size={24} className="mb-2 opacity-50" />
                      <span className="text-[12px]">暂无存档，点击"生成今日计划"将自动存档到 Obsidian</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {archiveFiles.map((file, i) => {
                        const isDaily = file.path.includes("/Daily/");
                        const isWeekly = file.path.includes("/Weekly/");
                        const isMonthly = file.path.includes("/Monthly/");
                        const typeLabel = isDaily ? "日计划" : isWeekly ? "周计划" : isMonthly ? "月计划" : "运营";
                        const typeColor = isDaily ? "#38BDF8" : isWeekly ? "#34D399" : "#A78BFA";
                        return (
                          <button
                            key={`archive-${i}`}
                            onClick={() => openArchiveFile(file)}
                            className="text-left rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 hover:border-cyan-500/30 hover:bg-cyan-500/[0.03] transition-colors group"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[12px] text-gray-200 font-medium truncate">
                                {file.name.replace(/\.md$/i, "")}
                              </span>
                              <span
                                className="text-[9px] font-mono px-1.5 py-0.5 rounded flex-shrink-0 ml-2"
                                style={{ background: `${typeColor}20`, color: typeColor }}
                              >
                                {typeLabel}
                              </span>
                            </div>
                            <div className="text-[9px] font-mono text-gray-600">
                              {file.path}
                            </div>
                            {file.mtime && (
                              <div className="text-[9px] font-mono text-gray-700 mt-0.5">
                                更新于 {file.mtime}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : (
                  /* 生成内容历史列表（脚本 + 媒体包） */
                  contentHistoryLoading ? (
                    <div className="flex items-center justify-center py-12 text-gray-500">
                      <Loader2 size={16} className="animate-spin mr-2" />
                      <span className="text-[12px]">正在从 Obsidian Vault 加载生成内容...</span>
                    </div>
                  ) : contentHistoryFiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-600">
                      <FileSearch size={24} className="mb-2 opacity-50" />
                      <span className="text-[12px]">暂无生成内容，到「媒体工作室」生成内容后将自动存档</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {contentHistoryFiles.map((file, i) => {
                        // 根据路径判断类型
                        const isScript = file.path.includes("/Scripts/");
                        const isPackage = file.path.includes("/ContentPackages/");
                        const typeLabel = isScript ? "脚本" : isPackage ? "媒体包" : "内容";
                        const typeColor = isScript ? "#A855F7" : isPackage ? "#EC4899" : "#6B7280";
                        // 从文件名提取平台信息（格式：{平台}-{主题}-{时间戳}.md）
                        const platformMatch = file.name.match(/^(wechat|douyin_enterprise|douyin_personal|xiaohongshu|zhihu|bilibili|live_stream)-/);
                        const platformLabel = platformMatch ? {
                          wechat: "微信公众号",
                          douyin_enterprise: "抖音企业号",
                          douyin_personal: "抖音个人号",
                          xiaohongshu: "小红书",
                          zhihu: "知乎",
                          bilibili: "B站",
                          live_stream: "每日直播",
                        }[platformMatch[1]] || platformMatch[1] : null;
                        return (
                          <button
                            key={`content-${i}`}
                            onClick={() => openArchiveFile(file)}
                            className="text-left rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 hover:border-purple-500/30 hover:bg-purple-500/[0.03] transition-colors group"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[12px] text-gray-200 font-medium truncate flex-1">
                                {file.name.replace(/\.md$/i, "")}
                              </span>
                              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                {platformLabel && (
                                  <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-400">
                                    {platformLabel}
                                  </span>
                                )}
                                <span
                                  className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                                  style={{ background: `${typeColor}20`, color: typeColor }}
                                >
                                  {typeLabel}
                                </span>
                              </div>
                            </div>
                            <div className="text-[9px] font-mono text-gray-600 truncate">
                              {file.path}
                            </div>
                            {file.mtime && (
                              <div className="text-[9px] font-mono text-gray-700 mt-0.5">
                                生成于 {file.mtime}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )
                )}
              </div>

              {/* 底部 */}
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[9px] font-mono text-gray-600">
                  {archiveTab === "plans"
                    ? "存档路径：10_Operations（运营层）/Daily/ · 自动写入 Obsidian Vault"
                    : "存档路径：XEAOS/Daily/Scripts/ + XEAOS/ContentPackages/ · 媒体工作室生成"}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
